<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { listPosts, takedownPost, restorePost, type PostRow } from '../api';

const total = ref(0);
const items = ref<PostRow[]>([]);
const loading = ref(false);
const statusFilter = ref<string>('');
const qFilter = ref('');
const page = ref(1);
const pageSize = 20;

async function reload() {
  loading.value = true;
  try {
    const r = await listPosts({
      status: statusFilter.value || undefined,
      q: qFilter.value || undefined,
      take: pageSize,
      skip: (page.value - 1) * pageSize,
    });
    total.value = r.total;
    items.value = r.items;
  } catch (e) {
    ElMessage.error(normalize(e));
  } finally {
    loading.value = false;
  }
}

async function onTakedown(row: PostRow) {
  try {
    const { value } = await ElMessageBox.prompt(
      '下架后该帖将不再展示(BLOCKED)。请填写下架原因(可留空,记入审计):',
      `下架帖子 ${row.id}`,
      {
        inputType: 'textarea',
        confirmButtonText: '确认下架',
        cancelButtonText: '取消',
        inputValidator: () => true,
      },
    );
    await takedownPost(row.id, (value || '').trim() || undefined);
    ElMessage.success('已下架');
    reload();
  } catch (e) {
    if ((e as { message?: string })?.message === 'cancel' || e === 'cancel') return;
    ElMessage.error(normalize(e));
  }
}

async function onRestore(row: PostRow) {
  try {
    await ElMessageBox.confirm(`确认恢复帖子 ${row.id}?恢复后将重新展示(VISIBLE)。`, '恢复帖子', {
      type: 'warning',
    });
    await restorePost(row.id);
    ElMessage.success('已恢复');
    reload();
  } catch (e) {
    if ((e as { message?: string })?.message === 'cancel' || e === 'cancel') return;
    ElMessage.error(normalize(e));
  }
}

function statusTag(s: string) {
  const map: Record<string, { type: 'info' | 'success' | 'warning' | 'danger'; label: string }> = {
    VISIBLE: { type: 'success', label: '展示中' },
    HIDDEN_PENDING_REVIEW: { type: 'warning', label: '机审待复核' },
    BLOCKED: { type: 'danger', label: '已下架' },
  };
  return map[s] ?? { type: 'info' as const, label: s };
}

function normalize(e: unknown) {
  const err = e as { message?: string; code?: string };
  return err?.message || err?.code || '未知错误';
}

function formatTime(s: string | null): string {
  if (!s) return '-';
  return new Date(s).toLocaleString('zh-CN');
}

function textPreview(text: string): string {
  return text.length > 60 ? `${text.slice(0, 60)}…` : text;
}

watch([statusFilter, qFilter], () => {
  page.value = 1;
  reload();
});

onMounted(reload);
</script>

<template>
  <div class="posts-pane">
    <div class="filters">
      <el-select v-model="statusFilter" placeholder="状态" clearable style="width: 160px">
        <el-option label="展示中" value="VISIBLE" />
        <el-option label="机审待复核" value="HIDDEN_PENDING_REVIEW" />
        <el-option label="已下架" value="BLOCKED" />
      </el-select>
      <el-input
        v-model="qFilter"
        placeholder="搜正文 / 作者昵称"
        clearable
        style="width: 280px; margin-left: 8px"
      />
      <el-button @click="reload" style="margin-left: 8px">刷新</el-button>
      <span class="total">共 {{ total }} 条</span>
    </div>

    <el-table :data="items" v-loading="loading" stripe>
      <el-table-column label="正文" min-width="260">
        <template #default="{ row }">
          <div class="post-text">{{ textPreview(row.text) }}</div>
          <span v-if="row.media.length" class="muted">🖼 {{ row.media.length }} 图</span>
        </template>
      </el-table-column>
      <el-table-column label="作者" width="150">
        <template #default="{ row }">
          <div>{{ row.author?.displayName || '-' }}</div>
          <span class="muted">{{ row.author?.schoolName || '' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="120">
        <template #default="{ row }">
          <el-tag :type="statusTag(row.visibleStatus).type" size="small">
            {{ statusTag(row.visibleStatus).label }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="互动" width="120">
        <template #default="{ row }">👍 {{ row.likeCount }} · 💬 {{ row.commentCount }}</template>
      </el-table-column>
      <el-table-column label="发布" width="160">
        <template #default="{ row }">{{ formatTime(row.createdAt) }}</template>
      </el-table-column>
      <el-table-column label="操作" fixed="right" width="130">
        <template #default="{ row }">
          <el-button
            v-if="row.visibleStatus !== 'BLOCKED'"
            size="small"
            type="danger"
            @click="onTakedown(row)"
          >下架</el-button>
          <el-button
            v-else
            size="small"
            type="warning"
            @click="onRestore(row)"
          >恢复</el-button>
        </template>
      </el-table-column>
    </el-table>

    <el-pagination
      v-model:current-page="page"
      :page-size="pageSize"
      :total="total"
      layout="prev, pager, next, total"
      @current-change="reload"
      style="margin-top: 16px; justify-content: flex-end"
    />
  </div>
</template>

<style scoped>
.posts-pane { padding: 4px 0; }
.filters { display: flex; align-items: center; margin-bottom: 16px; }
.total { margin-left: auto; color: #86868b; font-size: 13px; }
.post-text { color: #1d1d1f; line-height: 1.45; }
.muted { color: #86868b; font-size: 12px; }
</style>
